import { useEffect, useMemo } from 'react';

import cx from 'classnames';
import { useSelector } from 'react-redux';

import { FormBuilder } from 'components/Layouts/Form/FormBuilder';
import { getItineraryNavLinks } from 'components/Pages/Admin/Page/Landing/utils';
import Builder from 'components/Pages/Admin/shared/ItinerariesItemLanding/Builder';
import formBuilderStyles from 'components/Pages/Admin/shared/ItinerariesItemLanding/index.module.css';
import { LANDING_PAGES_CONTAINER_ID } from 'components/shared/LandingPage/components/Grid';
import { MaybeVirtualizer } from 'components/shared/LandingPage/Virtualizer';
import Text from 'components/shared/Typography/Text';
import { useCustomFonts, useCustomStyles } from 'hooks/useCustomFront';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { saveAsDOCX } from 'utils/filesDownloaders/docx';
import { saveAsPDF } from 'utils/filesDownloaders/pdf';
import isNoData from 'utils/isNoData';
import { getLandingTheme, LANDING_THEMES } from 'utils/itineraries/functions';
import { removeAIBlocksFromProposal, useItinerariesItemSaveHandler } from 'utils/itineraries/functions/builderUtils';
import { prepareProposalPages } from 'utils/itineraries/guestPortal/prepareGuestPortal';

import ItinerariesItemActionButtons from '../../../ItinerariesItemBuilder/ActionButtons';
import BuilderPagePreview from '../../../shared/BuilderPagePreview';
import { useProposalDifferenceHandler } from '../../../shared/functions';
import { getDefaultActiveAnchor, useContentPagePreview } from '../../../shared/ItinerariesItemLanding/functions';
import usePagesInitializer from './../functions';

const GPLandingBuilderV1 = () => {
  const rawGuestPortal = useSelector(itinerariesItemDataSelector);
  const handleSave = useItinerariesItemSaveHandler({
    onPrepareSaveData: (newItinerary) => {
      return removeAIBlocksFromProposal(newItinerary);
    },
    onAfterSave: (newItinerary) => {
      setNewPages(newItinerary.landing.pages);
    },
  });
  const { initialized } = usePagesInitializer(rawGuestPortal);
  const guestPortal = useMemo(
    () => prepareProposalPages(rawGuestPortal, { hideTravelWalletIfEmpty: true }),
    [rawGuestPortal],
  );
  const pages = guestPortal.landing?.pages;
  const { updatedPages, setNewPages } = useProposalDifferenceHandler(pages);
  const landingTheme = getLandingTheme(guestPortal.itinerary);
  useCustomStyles({ css: guestPortal.itinerary?.customStyles, customId: guestPortal.itinerary.id });
  useCustomFonts({ landingItem: guestPortal.itinerary, customId: guestPortal.itinerary.id });
  const { isTabletOrMobile } = useMediaBreakpoints();

  useEffect(() => {
    if (landingTheme === LANDING_THEMES.default) {
      return;
    }

    const themeToApply = LANDING_THEMES[landingTheme];

    if (themeToApply) {
      document.body.classList.add(`landing-theme-${themeToApply}`, 'landing-guest-builder');
    }

    return () => {
      if (themeToApply) {
        document.body.classList.remove(`landing-theme-${themeToApply}`, 'landing-guest-builder');
      }
    };
  }, [landingTheme]);

  const navLinks = getItineraryNavLinks(pages);

  const { withContentPagePreview, pagePreviewId } = useContentPagePreview();
  const defaultActiveAnchor = getDefaultActiveAnchor(pages, navLinks, pagePreviewId);

  if (!initialized) return null;

  if (isNoData(pages)) return <Text>No Data</Text>;

  return (
    <FormBuilder
      withNav={true}
      navLinks={navLinks}
      isItinerariesItem
      withMenuSettingOffset={false}
      className={cx(
        formBuilderStyles.formBuilder,
        formBuilderStyles.formBuilderWithoutSettingsBar,
        `custom-main-${landingTheme}`,
      )}
      sectionClassName={`custom-section-${landingTheme}`}
      landingTheme={landingTheme}
      defaultActiveAnchor={defaultActiveAnchor}
    >
      <MaybeVirtualizer
        getEnabled={() => !isTabletOrMobile && landingTheme === 'default'}
        count={pages.length}
        getContentRef={() => ({ current: document.getElementById(LANDING_PAGES_CONTAINER_ID) })}
        getRerenderDeps={({ visibleItems }) => [!visibleItems || visibleItems.length === 0 ? null : rawGuestPortal]}
      >
        {({ getPageVirtualized } = {}) => (
          <>
            {pages.map((page, i) => (
              <Builder
                key={i}
                landingData={page}
                anchor={navLinks[i]}
                screens={navLinks}
                pageIndex={i}
                page={guestPortal}
                landingTheme={landingTheme}
                virtualized={getPageVirtualized?.(i)}
              />
            ))}
          </>
        )}
      </MaybeVirtualizer>
      <ItinerariesItemActionButtons
        onSave={() => handleSave({ pages: updatedPages })}
        onPDF={() =>
          handleSave({ pages: updatedPages }).then(() =>
            saveAsPDF({
              item: guestPortal,
              itinerariesItemType: guestPortal.type,
            }),
          )
        }
        onDOCX={(themeId, etag) =>
          handleSave({ pages: updatedPages }).then(() =>
            saveAsDOCX({
              item: guestPortal,
              itinerariesItemType: guestPortal.type,
              themeId,
              etag,
            }),
          )
        }
      />
      {withContentPagePreview && <BuilderPagePreview pagePreviewId={pagePreviewId} />}
    </FormBuilder>
  );
};

export default GPLandingBuilderV1;
